import {
  IAimProfilePatch,
  IArcSettingsPatch,
  ILearnedProfileChange,
  IUser,
  IUserDisplaySettings,
} from '@common/interfaces/user'
import Base, { Wrapped } from './base'

export default class Auth extends Base {
  profile() {
    return this.apiClient.get('users/me')
  }

  login(email: string, password: string) {
    return this.apiClient.post('auth/login', { email, password })
  }

  // Google authentication methods
  googleAuth(token: string) {
    return this.apiClient.post('auth/google', { token })
  }

  linkGoogle(token: string) {
    return this.apiClient.post('auth/link-google', { token })
  }

  unlinkGoogle() {
    return this.apiClient.post('auth/unlink-google')
  }

  microsoftAuth(token: string) {
    return this.apiClient.post('auth/microsoft', { token })
  }

  linkMicrosoft(token: string) {
    return this.apiClient.post('auth/link-microsoft', { token })
  }

  unlinkMicrosoft() {
    return this.apiClient.post('auth/unlink-microsoft')
  }

  confirm(hash: string) {
    return this.apiClient.post(`actions/${hash}`)
  }

  resetPassword(email: string) {
    return this.apiClient.post('users/reset-password', { email })
  }

  setPassword(hash: string, password: string, confirmPassword: string) {
    return this.apiClient.post(`actions/${hash}`, {
      data: { password, confirmPassword },
    })
  }

  updatePassword(password: string | undefined, newPassword: string) {
    return this.apiClient.post('users/update-password', {
      data: { password, newPassword },
    })
  }

  updateProfile(data: object | FormData) {
    return this.apiClient.put('users', data)
  }

  updateNotificationSettings(data: Partial<IUser>) {
    return this.apiClient.post('users/notification-settings', data)
  }

  // Partial: callers send only the setting they changed.
  updateDisplaySettings(data: Partial<IUserDisplaySettings>) {
    return this.apiClient.post('users/display-settings', data)
  }

  /**
   * Who is online right now. Seeds the presence store, which otherwise only
   * learns about people from transition pushes it happened to be listening for.
   */
  listPresence() {
    return this.apiClient.get('users/presence')
  }

  // Partial: the settings tab sends the nickname, the avatar-menu quick-set sends
  // the status, and neither should clobber the other.
  updateAimProfile(data: IAimProfilePatch) {
    return this.apiClient.post('users/aim-profile', data)
  }

  // Partial: each Arc Settings box sends only the preference it changed.
  updateArcSettings(data: IArcSettingsPatch) {
    return this.apiClient.post('users/arc-settings', data)
  }

  readVoiceTone() {
    return this.apiClient.get('users/voice-tone')
  }

  updateVoiceTone(data: { aiVoiceTone: string }) {
    return this.apiClient.post('users/voice-tone', data)
  }

  regenerateVoiceTone() {
    return this.apiClient.post('users/voice-tone/regenerate')
  }

  listLearnedProfileHistory() {
    return this.apiClient.get('users/learned-profile/history') as Wrapped<ILearnedProfileChange[]>
  }

  revertLearnedProfile(id: string) {
    return this.apiClient.post(`users/learned-profile/revert/${id}`) as Wrapped<IUser>
  }

  processInvitation(hash: string) {
    return this.apiClient.post(`actions/${hash}`)
  }

  checkPasswordSetup(hash: string) {
    return this.apiClient.get(`auth/setup-password/${hash}`)
  }

  setupPassword(
    hash: string,
    {
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
    }: {
      firstName: string
      lastName: string
      email: string
      phone: string
      password: string
      confirmPassword: string
    },
  ) {
    return this.apiClient.post(`auth/setup-password/${hash}`, {
      firstName,
      lastName,
      email,
      phone,
      password,
      confirmPassword,
    })
  }

  googleInvitationAuth(
    hash: string,
    {
      token,
    }: {
      token: string
    },
  ) {
    return this.apiClient.post(`auth/google-invitation/${hash}`, {
      token,
    })
  }

  microsoftInvitationAuth(
    hash: string,
    {
      token,
      firstName,
      lastName,
      phone,
    }: {
      token: string
      firstName: string
      lastName: string
      phone?: string
    },
  ) {
    return this.apiClient.post(`auth/microsoft-invitation/${hash}`, {
      token,
      firstName,
      lastName,
      phone,
    })
  }

  async logout(token: string) {
    if (token) {
      return this.apiClient.post('auth/logout', { token }) as Promise<unknown>
    }
    return false
  }

  loginAsClient(clientId: string) {
    return this.apiClient.post('auth/login-as-client', { clientId })
  }

  logoutAsClient() {
    return this.apiClient.post('auth/logout-as-client')
  }

  switchClient(clientId: string) {
    return this.apiClient.post('auth/switch-client', { clientId })
  }
}
